import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import BookingCta from "../components/BookingCta";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSiteConfig } from "../lib/site";

const REASONS = [
  {
    n: "01",
    title: "Time to properly unwind",
    body: "An unhurried session in a calm room, with space before and after to settle. Many people come simply to step out of a busy week for a while.",
  },
  {
    n: "02",
    title: "Attention to muscular tension",
    body: "Shoulders, neck, back - the places that quietly hold a working life. Dorothy shapes the work around where you carry tension and the pressure you prefer.",
  },
  {
    n: "03",
    title: "An hour that belongs to you",
    body: "No rushing, no routine done to order. You talk, you decide together, and the session follows what you actually need on the day.",
  },
];

function BrollSection() {
  const videoRef = useRef(null);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches && videoRef.current) {
      videoRef.current.removeAttribute("autoplay");
      videoRef.current.pause();
    }
  }, []);
  return (
    <section className="mx-auto max-w-6xl px-6 pb-16 md:pb-24" data-testid="home-broll">
      <p className="eyebrow text-center">A quiet look inside the practice</p>
      <video
        ref={videoRef}
        className="mt-6 aspect-video w-full rounded-2xl object-cover shadow-[0_24px_60px_rgba(66,82,68,0.18)]"
        src="/video/dorothy-broll.mp4"
        poster="/video/broll-poster.jpg"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-label="A quiet film of Dorothy preparing her treatment room, walking in the woods and sitting by the window"
      />
    </section>
  );
}

export default function Home() {
  const config = useSiteConfig();
  const b = config.business;

  return (
    <>
      <Seo
        title="Dorothy Read Healing | Bodywork & Reiki in East Stroudsburg"
        description="Explore personalized bodywork and gentle energy work with Dorothy Read at Wellspring Holistic Center in East Stroudsburg. View rates and request a visit."
        path="/"
      />

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-6 pb-16 pt-10 md:pb-24 md:pt-16" data-testid="home-hero">
        <div className="grid items-center gap-10 md:grid-cols-12">
          <div className="animate-fade-in md:col-span-7">
            <h1 className="font-serif text-5xl font-semibold leading-[1.05] tracking-tight text-primary md:text-6xl">
              Personalized bodywork and gentle energy work
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-foreground/90">
              A thoughtful session shaped around you, with time to talk, settle in and choose an
              approach that feels comfortable. Dorothy brings {b.experience_statement} to her
              individualized practice.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <BookingCta location="hero" testid="hero-schedule-appointment" />
            </div>
          </div>
          <div className="relative animate-fade-in md:col-span-5" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -left-6 -top-6 hidden h-40 w-40 rounded-full bg-secondary/70 md:block" aria-hidden />
            <img
              src="/images/woodland-walk.jpg"
              alt="Dorothy walking a quiet woodland path in morning light"
              className="relative aspect-[4/5] w-full rounded-2xl object-cover shadow-[0_24px_60px_rgba(66,82,68,0.18)]"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* B-roll film */}
      <BrollSection />

      {/* Intro to Dorothy */}
      <section className="bg-secondary/50">
        <div className="mx-auto grid max-w-6xl items-center gap-10 px-6 py-16 md:grid-cols-12 md:py-24">
          <div className="md:col-span-4">
            <img
              src="/images/dorothy-portrait.jpg"
              alt="Dorothy Read seated in soft natural light"
              className="aspect-[4/5] w-full rounded-2xl object-cover object-top shadow-[0_16px_44px_rgba(66,82,68,0.14)]"
              loading="lazy"
            />
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="eyebrow">Meet Dorothy</p>
            <h2 className="mt-4 font-serif text-4xl font-semibold tracking-tight text-primary md:text-5xl">
              A quiet practice built on attention
            </h2>
            <p className="mt-6 text-lg leading-relaxed">
              Dorothy Read has spent over 30 years listening first and working second.
              Every session begins with a conversation - how you are feeling, where you hold tension,
              what kind of touch you are comfortable with - and the work follows from there.
            </p>
            <p className="mt-4 text-lg leading-relaxed">
              Her practice blends therapeutic bodywork with gentle energy work, offered plainly and
              without expectation. You are welcome whatever you believe, and whatever brings you through the door.
            </p>
            <Link
              to="/about"
              data-testid="home-about-link"
              className="mt-6 inline-flex min-h-[44px] items-center gap-2 font-semibold text-accent underline-offset-4 hover:underline"
            >
              More about Dorothy <ArrowRight size={18} aria-hidden />
            </Link>
          </div>
        </div>
      </section>

      {/* Three reasons */}
      <section className="mx-auto max-w-6xl px-6 py-16 md:py-24" data-testid="home-reasons">
        <h2 className="max-w-2xl font-serif text-4xl font-semibold tracking-tight text-primary md:text-5xl">
          Why people come
        </h2>
        <div className="mt-12 divide-y divide-border border-y border-border">
          {REASONS.map((r) => (
            <div key={r.n} className="grid gap-3 py-8 md:grid-cols-12 md:gap-8" data-testid={`reason-${r.n}`}>
              <span className="font-serif text-3xl font-medium text-accent md:col-span-2">{r.n}</span>
              <h3 className="font-serif text-2xl font-semibold text-primary md:col-span-4">{r.title}</h3>
              <p className="text-lg leading-relaxed text-foreground/90 md:col-span-6">{r.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Reassurance band */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-4xl px-6 py-16 text-center md:py-20">
          <p className="font-serif text-3xl font-medium leading-snug md:text-4xl" data-testid="reassurance-quote">
            &ldquo;You do not need to choose a technique before you arrive. Dorothy will talk with you
            about your preferences and the approaches she offers.&rdquo;
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-secondary/50">
        <div className="mx-auto max-w-3xl px-6 py-16 md:py-24" data-testid="home-faq">
          <h2 className="font-serif text-4xl font-semibold tracking-tight text-primary">Common questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            <AccordionItem value="q2" data-testid="faq-cost">
              <AccordionTrigger className="text-left text-lg font-semibold">What does a session cost?</AccordionTrigger>
              <AccordionContent className="text-lg leading-relaxed">
                A 90-minute session is $125. If you take a package, the per-visit cost comes down by
                10% for each visit added - two visits $225, three visits $300.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q3" data-testid="faq-booking">
              <AccordionTrigger className="text-left text-lg font-semibold">How do I make an appointment?</AccordionTrigger>
              <AccordionContent className="text-lg leading-relaxed">
                Tap &ldquo;Schedule an appointment&rdquo; anywhere on this site and leave your details.
                Dorothy replies to every message personally, in her own time.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q4" data-testid="faq-location">
              <AccordionTrigger className="text-left text-lg font-semibold">Where are you located?</AccordionTrigger>
              <AccordionContent className="text-lg leading-relaxed">
                At {b.address_lines.join(", ")} - a short drive from Stroudsburg and the surrounding Pocono area.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="q5" data-testid="faq-cancel">
              <AccordionTrigger className="text-left text-lg font-semibold">What if I need to change my appointment?</AccordionTrigger>
              <AccordionContent className="text-lg leading-relaxed">
                Life happens. {config.policies.cancellation} Just let Dorothy know through the schedule form.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-6 py-16 text-center md:py-24" data-testid="home-final-cta">
        <h2 className="font-serif text-4xl font-semibold tracking-tight text-primary md:text-5xl">
          Ready when you are
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-lg text-foreground/90">
          Schedule an appointment - Dorothy will take it from there.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-4">
          <BookingCta location="final_cta" testid="final-schedule-appointment" />
        </div>
      </section>
    </>
  );
}
